import { FunctionComponent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useMutation } from '@apollo/client'
import { LINK_TRUSTEE, SET_MAIN_TRUSTEE, UPDATE_TRUSTEE_LINK } from '@/modules/API/daycareGroup/trusteeLinkHandler'
import styles from './addAdminDialog.module.scss'
import UserInput from '@/components/_custom/UserInput/userInput'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface AddAdminDialogProps {
    open: boolean
    handleClose: () => void
    groupId: number
    refetchGroups: () => void
}

const AddAdminDialog: FunctionComponent<AddAdminDialogProps> = ({
    open,
    handleClose,
    groupId,
    refetchGroups
}: AddAdminDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [adminEmail, setAdminEmail] = useState<string>('')
    const [validEmail, setValidEmail] = useState<boolean>(false)
    const [adminPhone, setAdminPhone] = useState<string>('')
    const [adminLastname, setAdminLastname] = useState<string>('')
    const [adminFirstname, setAdminFirstname] = useState<string>('')

    const [isMainAdmin, setIsMainAdmin] = useState<boolean>(false)
    const [canWrite, setCanWrite] = useState<boolean>(false)

    const [createDaycareTrustee] = useMutation(LINK_TRUSTEE, {
        variables: {
            groupId: groupId,
            email: adminEmail,
            trustee: {
                phone: adminPhone,
                lastname: adminLastname,
                firstname: adminFirstname
            }
        },
        onCompleted: ({ linkTrustee }) => {
            if (isMainAdmin || canWrite) {
                if (isMainAdmin)
                    setMainTrustee({
                        variables: {
                            groupId: groupId,
                            trusteeId: Number(linkTrustee)
                        }
                    })
                if (canWrite && linkTrustee) {
                    updateTrusteeLink({
                        variables: {
                            groupId: groupId,
                            trusteeId: Number(linkTrustee),
                            canWrite: canWrite
                        }
                    })
                }
            } else {
                showNotificationSuccess(t('adminAdded'))

                refetchGroups()
                handleClose()
            }
        },
        onError: () => {
            showNotificationError(t('errorAddingAdmin'))
        }
    })

    const [setMainTrustee] = useMutation(SET_MAIN_TRUSTEE, {
        onCompleted: () => {
            showNotificationSuccess(t('adminAdded'))

            refetchGroups()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorAddingAdmin'))
        }
    })

    const [updateTrusteeLink] = useMutation(UPDATE_TRUSTEE_LINK, {
        onCompleted: () => {
            showNotificationSuccess(t('adminAdded'))

            refetchGroups()
            handleClose()
        },
        onError: () => {
            showNotificationError(t('errorAddingAdmin'))
        }
    })

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('addAdmin')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <UserInput
                        label={t('lastname')}
                        className={styles.UserInput}
                        value={adminLastname}
                        setValue={setAdminLastname}
                        format={'upper'}
                        required
                    />
                    <UserInput
                        label={t('firstname')}
                        className={styles.UserInput}
                        value={adminFirstname}
                        setValue={setAdminFirstname}
                        format={'pascal'}
                        required
                    />
                    <UserInput
                        label={t('email')}
                        className={styles.UserInput}
                        value={adminEmail}
                        setValue={setAdminEmail}
                        verify={'email'}
                        type='text'
                        unallowedCharacters={'email'}
                        setValid={setValidEmail}
                        required
                    />
                    <UserInput
                        label={t('phone')}
                        className={styles.UserInput}
                        value={adminPhone}
                        setValue={setAdminPhone}
                        format={'phone'}
                        required
                    />
                </div>
                <div className={styles.CheckBoxContainer}>
                    <Checkbox
                        className={styles.CheckBox}
                        text={t('isMainAdmin')}
                        checked={isMainAdmin}
                        onChange={setIsMainAdmin}
                    />
                    <Checkbox
                        className={styles.CheckBox}
                        text={t('canWrite')}
                        checked={canWrite}
                        onChange={setCanWrite}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'add'}
                        onClick={createDaycareTrustee}
                        disabled={!validEmail || !adminPhone || !adminLastname || !adminFirstname}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default AddAdminDialog
